import type { ComponentType } from 'react';

import { DownloadOptionsStep } from '../onboarding-step/download-options-step';
import { PersonaUseCaseStep } from '../onboarding-step/persona-use-case-step';
import { WelcomeStep } from '../onboarding-step/welcome-step';

// Personas
export type UseCase = 'work' | 'personal' | 'education';
export const UseCaseOrder: readonly UseCase[] = ['work', 'education', 'personal'];

export const UseCaseIcons: Record<UseCase, string> = {
	work: 'https://dam-cdn.atl.orangelogic.com/AssetLink/b5ha7xmmro2h3w0r30atj02a2b406433.svg',
	education: 'https://dam-cdn.atl.orangelogic.com/AssetLink/0mv6p2041shaqa5g5s10475cf8h0285o.svg',
	personal: 'https://dam-cdn.atl.orangelogic.com/AssetLink/ck24jw170xw658r3bgvatec6x23xtq47.svg',
};

export const UseCaseLabels: Record<UseCase, string> = {
	work: 'Work',
	education: 'Education',
	personal: 'Personal',
};

export type Role =
	| 'Executive'
	| 'Sales'
	| 'Product'
	| 'Support'
	| 'Marketing'
	| 'Design'
	| 'Operations'
	| 'Engineering'
	| 'Customer Success'
	| 'Human Resources';

export type RoleOption = { value: Role; valueProp: string };

export const RoleOptions: readonly RoleOption[] = [
	{ value: 'Executive', valueProp: 'Executives use Loom to send out company updates.' },
	{ value: 'Sales', valueProp: 'Sales teams use Loom for personal outreach videos.' },
	{ value: 'Product', valueProp: 'Product teams use Loom to provide context.' },
	{ value: 'Support', valueProp: 'Support teams use Loom to record how-to videos.' },
	{ value: 'Marketing', valueProp: 'Marketing teams use Loom to promote new features.' },
	{ value: 'Design', valueProp: 'Designers use Loom to walk through designs.' },
	{ value: 'Operations', valueProp: 'Operations teams use Loom to provide feedback.' },
	{ value: 'Engineering', valueProp: 'Engineers use Loom to demo new features.' },
	{
		value: 'Customer Success',
		valueProp: 'Customer Success teams use Loom to onboard new customers.',
	},
	{ value: 'Human Resources', valueProp: 'HR teams use Loom to onboard new teammates.' },
];

export const EducationValueProp =
	'Teachers use Loom to record lesson plans, to teach remotely, and to connect with students asynchronously.';
export const PersonalValueProp =
	'People use Loom to send video messages to friends and family, record personal projects, keepsakes, and more.';

// Download
export type Recorder = 'extension' | 'windows' | 'intel' | 'apple' | 'none';

// Steps
/**
 * Analytics payload a step may report back to the step manager when the
 * user completes it. Both fields are optional so steps that don't collect
 * either signal (e.g. the welcome screen) can keep calling `onContinue()`
 * with no arguments.
 */
export type StepCompletionPayload = {
	action?: StepActions;
	persona?: UseCase;
};

export type OnboardingStepProps = {
	/**
	 * Primary action for every onboarding screen. The step manager decides
	 * whether that advances to the next step or finishes the flow.
	 *
	 * Steps that collected an `action` or `persona` from the user should
	 * pass them here so they end up on the `onboardingStep completed`
	 * analytics event.
	 */
	onContinue: (payload?: StepCompletionPayload) => void;
};

export const ONBOARDING_STEPS = [
	'welcome-step',
	'persona-use-case-step',
	'download-options-step',
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/**
 * The onboarding flow the user went through. Kept as a union so adding a
 * new flow later forces every analytics call site to disambiguate.
 */
export type OnboardingFlow = 'default';

/**
 * Catalog of every onboarding screen. Add a new screen by defining its
 * constant above and mapping it here.
 */
export const ONBOARDING_STEP_COMPONENTS: Record<
	OnboardingStep,
	ComponentType<OnboardingStepProps>
> = {
	['welcome-step']: WelcomeStep,
	['persona-use-case-step']: PersonaUseCaseStep,
	['download-options-step']: DownloadOptionsStep,
} satisfies Record<string, ComponentType<OnboardingStepProps>>;

// Onboarding Data
//
// `useCase`, `role`, and `recorder` are all optional so a freshly created
// record starts with no user selections. Each step seeds its local state
// from whatever is (or isn't) here so navigating back and forth preserves
// prior answers without ever forcing a default the user didn't pick.
export type OnboardingData = {
	aaid: string;
	useCase?: UseCase;
	role?: Role;
	recorder?: Recorder;
};

// Analytics Default Attributes Type
export const onboardingSource = 'wac-provisioning';
export type StepActions =
	| 'skip'
	| 'download-chrome'
	| 'download-windows'
	| 'download-intel'
	| 'download-apple';

/**
 * Maps the internal `Recorder` id the user selects on the download step to
 * the `StepActions` value we report to analytics.
 */
export const RecorderToStepAction: Record<Recorder, StepActions> = {
	extension: 'download-chrome',
	windows: 'download-windows',
	intel: 'download-intel',
	apple: 'download-apple',
	none: 'skip',
};
export type DefaultAnalyticsAttributes = {
	flowId: OnboardingFlow;
	source: typeof onboardingSource;
};
export type DefaultStepAnalyticsAttributes = DefaultAnalyticsAttributes & {
	step: OnboardingStep;
	stepIndex: number;
};
